export type ChannelSide = 'home' | 'away' | 'national';

export type ListenOptionKey = 'away' | 'home' | 'national' | 'spanish';

type StreamKey = `${ListenOptionKey}Stream`;
type InternetStreamKey = `${ListenOptionKey}InternetStream`;
type StreamLinkKey = `${ListenOptionKey}StreamLink`;

export type ChannelStreams = Partial<Record<StreamKey | InternetStreamKey, number> & Record<StreamLinkKey, string>>;

export interface ResolvedChannel {
    channel?: number;
    isOnline: boolean;
}

export interface ListenOption {
    key: ListenOptionKey;
    label: string;
    channel?: number;
    internetChannel?: number;
    deepLink?: string;
}

const SIDE_STREAMS: Record<
    ChannelSide,
    { stream: StreamKey; internet: InternetStreamKey; link: StreamLinkKey } | undefined
> = {
    home: { stream: 'homeStream', internet: 'homeInternetStream', link: 'homeStreamLink' },
    away: { stream: 'awayStream', internet: 'awayInternetStream', link: 'awayStreamLink' },
    national: undefined,
};

const LISTEN_OPTIONS: {
    key: ListenOptionKey;
    label: string;
    stream: StreamKey;
    internet: InternetStreamKey;
    link: StreamLinkKey;
}[] = [
    { key: 'away', label: 'Away', stream: 'awayStream', internet: 'awayInternetStream', link: 'awayStreamLink' },
    { key: 'home', label: 'Home', stream: 'homeStream', internet: 'homeInternetStream', link: 'homeStreamLink' },
    {
        key: 'national',
        label: 'National',
        stream: 'nationalStream',
        internet: 'nationalInternetStream',
        link: 'nationalStreamLink',
    },
    {
        key: 'spanish',
        label: 'Espanol',
        stream: 'spanishStream',
        internet: 'spanishInternetStream',
        link: 'spanishStreamLink',
    },
];

export function resolveChannel(streams: ChannelStreams, side: ChannelSide): ResolvedChannel {
    const sideFields = SIDE_STREAMS[side];
    const sideStream = sideFields ? streams[sideFields.stream] : undefined;
    const sideInternetStream = sideFields ? streams[sideFields.internet] : undefined;

    if (sideStream) {
        return { channel: sideStream, isOnline: false };
    }
    if (streams.nationalStream) {
        return { channel: streams.nationalStream, isOnline: false };
    }
    if (sideInternetStream || streams.nationalInternetStream) {
        return { isOnline: true };
    }
    if (streams.spanishStream) {
        return { channel: streams.spanishStream, isOnline: false };
    }
    if (streams.spanishInternetStream) {
        return { isOnline: true };
    }

    return { isOnline: false };
}

export function toChannelLabel(resolved: ResolvedChannel): number | 'ONLINE' | '' {
    if (resolved.isOnline) {
        return 'ONLINE';
    }
    return resolved.channel ?? '';
}

export function resolvePlayerUrl(streams: ChannelStreams, side: ChannelSide): string | undefined {
    const sideFields = SIDE_STREAMS[side];
    const sideLink = sideFields ? streams[sideFields.link] : undefined;

    return sideLink || streams.nationalStreamLink || streams.spanishStreamLink;
}

export function buildListenOptions(streams: ChannelStreams): ListenOption[] {
    return LISTEN_OPTIONS.filter((option) => streams[option.stream] || streams[option.internet]).map((option) => ({
        key: option.key,
        label: option.label,
        channel: streams[option.stream],
        internetChannel: streams[option.internet],
        deepLink: streams[option.link],
    }));
}
